import { ENavGroupNames, ENavigateNames } from 'routes/constants';

// MUI Badge defaults are ~20px; scale down by 1.5x for header indicators.
export const INDICATOR_BADGE_SX = {
	'& .MuiBadge-badge': {
		minWidth: 13,
		height: 13,
		fontSize: '0.7rem',
		lineHeight: '13px',
		padding: '0 4px',
		top: '2px',
		right: '2px',
	},
} as const;

// Human-readable descriptions shown in the navigation tour for each top-level
// item or group item. Falls back to the item name when an entry is missing.
export const NAV_ITEM_TOUR_DESCRIPTIONS: Record<string, string> = {
	[ENavigateNames.DASHBOARD]: 'Analytics dashboards embedded from Superset.',
	[ENavigateNames.EM_MANAGEMENT]: 'Manage EAS Merchants and their settings.',
	[ENavigateNames.ORDER_MANAGEMENT]: 'Browse and manage customer orders.',
	[ENavigateNames.INVOICES]: 'View, search and process invoices.',
	[ENavigateNames.CREDIT_NOTES]: 'Credit notes issued to EAS Merchants.',
	[ENavigateNames.FISCAL_REPORTS]: 'Fiscal reports for tax authorities.',
	[ENavigateNames.EM_VAT_CORRECTIONS]: 'Verify and apply VAT corrections per EM.',
	[ENavigateNames.VAT_REIMBURSEMENT]: 'VAT reimbursement workflow and tracking.',
	[ENavigateNames.HMRC]: 'HMRC submissions and reports (UK tax authority).',
	[ENavigateNames.SWEDBANK_INTEGRATION]: 'SwedBank payment integration and matching.',
	[ENavigateNames.VAT_REPORTS]: 'Generate and download VAT reports.',
	[ENavigateNames.REPORTING_CENTER]: 'Available reports to download (mostly XLSX files).',
	[ENavigateNames.PAYMENT_GATEWAY]:
		'Card and Direct Debit payments: services, fees, schedule, payments, refunds and alerts.',
	[ENavigateNames.MONTHLY_FEE]: 'Monthly fees overview and management.',
	[ENavigateNames.SALES_PROCESSING]: 'Process and validate sales reports.',
	[ENavigateNames.ERROR_CORRECTION]: 'Sales data errors and correction workflows.',
	[ENavigateNames.HS_CODES_RETRIEVING]: 'Retrieve HS codes for products.',
	[ENavigateNames.GPSR]: 'GPSR product types, customers and invoices.',
	[ENavigateNames.SWISSPOST_CLIENTS]: 'SwissPost shipments and clients.',
	[ENavigateNames.REFERRALS]: 'Referrals and partner programs.',
	[ENavigateNames.EUDR]: 'EUDR registrations and submissions.',
	[ENavigateNames.EU_DDP_ORDERS]: 'EU DDP orders overview and details.',
	[ENavigateNames.PPWR]: 'PPWR settings of EAS Merchants.',
	[ENavigateNames.REFERENCE_TABLES]: 'Reference tables and dictionaries used across the app.',
	[ENavigateNames.NON_EU_VAT_RATES]: 'VAT rates for non-EU countries.',
	[ENavigateNames.SHOPIFY_SALES_CHANNELS]: 'Shopify sales channels reference table.',
	[ENavigateNames.RESOURCES]: 'IT directory: useful internal links and tools.',
};

// Description for each navigation group, shown when the corresponding dropdown
// is highlighted by the tour.
export const NAV_GROUP_TOUR_DESCRIPTIONS: Record<string, string> = {
	[ENavigateNames.INVOICE_MANAGEMENT]: 'Invoice Management — invoices and credit notes.',
	[ENavGroupNames.FINANCE_VAT]:
		'Finance & VAT — fiscal reports, VAT corrections, reimbursement, HMRC, SwedBank, VAT reports and the Reporting Center.',
	[ENavGroupNames.OPERATIONS]:
		'Operations — sales processing, error correction and HS code retrieval.',
	[ENavGroupNames.EXTRA_SERVICES]:
		'Extra Services — GPSR, SwissPost clients, referrals, EUDR and PPWR.',
	[ENavGroupNames.RESOURCES]: 'Resources — reference tables and the IT directory.',
};

// Joyride styles used by the tour, kept consistent with the rest of the app.
export const NAV_TOUR_STYLES = {
	options: { zIndex: 2000 },
	tooltip: {
		borderRadius: '12px',
		fontSize: '1rem',
	},
	buttonNext: {
		backgroundColor: '#016193',
		color: '#fff',
		borderRadius: '12px',
		fontSize: '0.9rem',
	},
	buttonSkip: {
		fontSize: '0.9rem',
		color: '#016193',
		fontWeight: 'bold' as const,
	},
	buttonBack: {
		color: '#016193',
		fontSize: '0.9rem',
		fontWeight: 'bold' as const,
	},
};

// DOM id prefixes used by the navigation tour.
export const NAV_IDS = {
	LOGO: 'nav-logo',
	ITEM_PREFIX: 'nav-item',
	GROUP_PREFIX: 'nav-group',
	GROUP_ITEM_PREFIX: 'nav-group-item',
	MORE_BUTTON: 'nav-more-button',
	USER_AVATAR: 'nav-user-avatar',
	TOUR_BUTTON: 'nav-tour-button',
};

// Bump this version when the navigation tour content meaningfully changes and
// you want every existing user to see the updated walkthrough once again.
export const NAV_TOUR_VERSION = 1;

// Per-user localStorage key marking that the user has already seen the
// auto-played navigation tour for the current `NAV_TOUR_VERSION`.
export const getNavTourSeenStorageKey = (userId: string | number): string =>
	`nav-tour-seen:v${NAV_TOUR_VERSION}:${userId}`;
